import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Info, RefreshCw, X } from "lucide-react";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { OverlayShell } from "@/components/ui/overlay";
import { ConversaLabels } from "@/lib/labels";
import { format } from "@/lib/format";

import { ConversationContext } from "./conversation-context";
import { FilesGallery } from "./files-gallery";
import { LiveWindow } from "./live-window";

export function CockpitConversation({ personId }: { personId: number }) {
	return <CockpitConversationContent key={personId} personId={personId} />;
}

function CockpitConversationContent({ personId }: { personId: number }) {
	const [filesOpen, setFilesOpen] = useState(false);
	const [contextOpen, setContextOpen] = useState(false);
	const {
		data: context,
		isPending,
		isError,
		refetch,
	} = useQuery(orpc.draft.context.queryOptions({ input: { person_id: personId } }));
	const { data: files } = useQuery(
		orpc.cockpit.filesCount.queryOptions({ input: { person_id: personId } }),
	);

	if (isPending) {
		return (
			<div className="flex h-full animate-pulse flex-col gap-4 p-5">
				<div className="h-12 rounded-lg bg-background" />
				<div className="flex-1 rounded-lg bg-background" />
				<div className="h-32 rounded-lg bg-background" />
			</div>
		);
	}

	if (isError) {
		return (
			<div role="alert" className="space-y-3 p-5 text-sm text-destructive">
				<p>{ConversaLabels.error}</p>
				<Button variant="outline" size="sm" onClick={() => refetch()}>
					<RefreshCw />
					Tentar de novo
				</Button>
				<Button asChild variant="ghost" size="sm">
					<Link to="/conversas">Voltar às conversas</Link>
				</Button>
			</div>
		);
	}

	const name = format.readableName(context.person.name) || context.person.wa_name || "Sem nome";

	return (
		<div data-component="cockpit-conversation" className="flex h-full min-h-0 min-w-0">
			<div className="flex min-h-0 min-w-0 flex-1 flex-col">
				<header className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
					<Button asChild size="icon" variant="ghost" className="md:hidden">
						<Link to="/conversas" aria-label="Voltar às conversas">
							<ArrowLeft />
						</Link>
					</Button>
					<div className="min-w-0 flex-1">
						<h2 className="truncate text-sm font-semibold">{name}</h2>
						<p className="mt-0.5 text-xs text-muted-foreground">
							{format.phone(context.person.phone)}
						</p>
					</div>
					<Button asChild size="sm" variant="ghost">
						<Link to="/pessoa/$id" params={{ id: personId }} search={{ tab: "cadastro" }}>
							Ver ficha
						</Link>
					</Button>
					<Button
						size="icon"
						variant="ghost"
						aria-label="Abrir contexto"
						className="xl:hidden"
						onClick={() => setContextOpen(true)}
					>
						<Info />
					</Button>
				</header>
				<LiveWindow
					context={context}
					mediaTotal={files?.total || 0}
					filesOpen={filesOpen}
					onToggleFiles={files?.total ? () => setFilesOpen(true) : null}
				/>
			</div>
			{!contextOpen && (
				<aside
					data-slot="conversa-work"
					className="hidden w-80 min-w-0 shrink-0 flex-col overflow-y-auto overscroll-contain border-l border-border bg-card xl:flex 2xl:w-96"
				>
					<header className="shrink-0 border-b border-border px-6 py-4">
						<h2 className="text-sm font-semibold text-foreground">Contexto</h2>
					</header>
					<ConversationContext personId={personId} />
				</aside>
			)}
			{contextOpen && (
				<OverlayShell
					label="Contexto da conversa"
					onClose={() => setContextOpen(false)}
					className="items-center justify-center p-4"
				>
					<div className="relative flex max-h-[85dvh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-card shadow-overlay">
						<header className="flex items-center justify-between border-b border-border px-5 py-4">
							<h2 className="font-semibold">Contexto da conversa</h2>
							<Button
								size="icon"
								variant="ghost"
								aria-label="Fechar contexto"
								onClick={() => setContextOpen(false)}
							>
								<X />
							</Button>
						</header>
						<div className="overflow-y-auto overscroll-contain">
							<ConversationContext personId={personId} />
						</div>
					</div>
				</OverlayShell>
			)}
			{filesOpen && <FilesGallery personId={personId} onClose={() => setFilesOpen(false)} />}
		</div>
	);
}
